import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Book, ArrowLeft, Mail } from "lucide-react";
import { useApp } from "@/contexts/AppContext";

interface AuthInfoScreenProps {
  title: string;
  subtitle: string;
  body: ReactNode;
  nextStep: string;
  primaryActionLabel: string;
  onPrimaryAction: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
}

export function AuthInfoScreen({
  title,
  subtitle,
  body,
  nextStep,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
}: AuthInfoScreenProps) {
  const { t } = useApp();
  const pageStyle = {
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--primary) 10%, var(--background)), color-mix(in srgb, var(--surface) 80%, var(--background)))",
  } as const;
  const mailBadgeStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--primary) 18%, var(--background))",
  } as const;
  const mailIconStyle = {
    color: "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as const;
  const infoPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--primary) 12%, var(--background))",
    borderColor:
      "color-mix(in srgb, var(--primary) 35%, var(--background))",
  } as const;
  const primaryTextStyle = {
    color: "var(--foreground)",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 78%, var(--background))",
  } as const;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={pageStyle}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="icon-box">
              <Book className="w-8 h-8 icon-primary" />
            </div>
            <div className="text-left">
              <h1 className="text-primary-heading">{t("header.title")}</h1>
              <p className="text-xs text-secondary">{t("header.subtitle")}</p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div
              className="mx-auto p-4 rounded-full w-fit mb-4"
              style={mailBadgeStyle}
            >
              <Mail className="w-8 h-8" style={mailIconStyle} />
            </div>
            <CardTitle className="text-center card-title-text">
              {title}
            </CardTitle>
            <CardDescription className="text-center card-description-text">
              {subtitle}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="mb-4" style={infoPanelStyle}>
              <AlertDescription className="text-sm" style={primaryTextStyle}>
                {body}
              </AlertDescription>
            </Alert>

            <p className="text-sm mb-6 text-center" style={secondaryTextStyle}>
              {nextStep}
            </p>

            <Button onClick={onPrimaryAction} className="w-full">
              {primaryActionLabel}
            </Button>
          </CardContent>
        </Card>

        {secondaryActionLabel && onSecondaryAction ? (
          <Button
            variant="ghost"
            onClick={onSecondaryAction}
            className="w-full mt-4 gap-2 text-secondary"
          >
            <ArrowLeft className="w-4 h-4" />
            {secondaryActionLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
